import {
    Add,
    Article,
    Code,
    DataObject,
    Difference,
    KeyboardArrowDown,
    KeyboardArrowRight,
    Refresh,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import SysColumn from "app/class/sys-column";
import { useSession } from "app/hooks/useSession";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import StudioAiPanel from "modules/studio/components/StudioAiPanel";
import StudioActivityRail from "modules/studio/components/StudioActivityRail";
import StudioResizeHandle from "modules/studio/components/StudioResizeHandle";
import StudioTopBar from "modules/studio/components/StudioTopBar";
import StudioWorkbench from "modules/studio/components/StudioWorkbench";
import {
    EXPLORER_PREFERENCE_KEY,
    EXPLORER_PREFERENCE_TABLE,
    STUDIO_ACTIVITY_WIDTH,
    STUDIO_LEFT_PANEL_DEFAULT_WIDTH,
    STUDIO_LEFT_PANEL_MAX_WIDTH,
    STUDIO_LEFT_PANEL_MIN_WIDTH,
    STUDIO_RESIZE_HANDLE_WIDTH,
    STUDIO_RIGHT_PANEL_DEFAULT_WIDTH,
    STUDIO_RIGHT_PANEL_MAX_WIDTH,
    STUDIO_RIGHT_PANEL_MIN_WIDTH,
} from "modules/studio/constants/studio-layout";
import {
    applyStudioProposals,
    fetchStudioConversation,
    fetchStudioContext,
    fetchStudioAiModels,
    fetchStudioSessions,
    generateStudioProposal,
    requestStudioArchitecture,
    StudioArchitectureResponse,
    StudioAiModel,
    StudioChangeProposal,
    StudioColumnMetadata,
    StudioConversationResponse,
    StudioProposalApplyResponse,
    StudioProposalResponse,
    StudioSession,
    updateStudioProposalStatus,
} from "modules/studio/services/studio-api";
import { StudioChatMessage, StudioExplorerPreference, StudioPanelMode, StudioRightPanelMode, StudioViewMode } from "modules/studio/types/studio-types";
import {
    clamp,
    isRequestCanceled,
    normalizePreferenceSet,
    packageChangeTableName,
    parseFieldValue,
    safeParseJson,
    safeRecordJson,
    setToArray,
    stringifyFieldValue,
    systemFields,
    toSysColumn,
    toSysTable,
} from "modules/studio/utils/studio-helpers";
import queryKeys, { NEW_RECORD_ID } from "shared/services/queryKeys";

import "./platform-studio-page.scss";

const PlatformStudioPage = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: userSession } = useSession();
    const architectureAbortControllerRef = useRef<AbortController | null>(null);
    const proposalAbortControllerRef = useRef<AbortController | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const currentPlatformPackageId = userSession?.currentPlatformPackageId;
    const [selectedTable, setSelectedTable] = useState("sys_tables");
    const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
    const [selectedFieldName, setSelectedFieldName] = useState<string | null>(null);
    const [draftRecord, setDraftRecord] = useState<Record<string, any>>({});
    const [fieldValue, setFieldValue] = useState("");
    const [recordJsonValue, setRecordJsonValue] = useState("{}");
    const [activeViewMode, setActiveViewMode] = useState<StudioViewMode>("editor");
    const [selectedAiModelId, setSelectedAiModelId] = useState("");
    const [editorError, setEditorError] = useState<string | null>(null);
    const [architectureResult, setArchitectureResult] = useState<StudioArchitectureResponse | null>(null);
    const [chatMessages, setChatMessages] = useState<StudioChatMessage[]>([]);
    const [selectedSessionId, setSelectedSessionId] = useState("");
    const [historyAutoLoaded, setHistoryAutoLoaded] = useState(false);
    const [leftPanelMode, setLeftPanelMode] = useState<StudioPanelMode>("explorer");
    const [rightPanelMode, setRightPanelMode] = useState<StudioRightPanelMode>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
    const [expandedTables, setExpandedTables] = useState<Set<string>>(() => new Set());
    const [expandedRecords, setExpandedRecords] = useState<Set<string>>(() => new Set());
    const [studioPreferenceApplied, setStudioPreferenceApplied] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
    const [pendingLeftPanelMode, setPendingLeftPanelMode] = useState<StudioPanelMode>(null);
    const [leftPanelWidth, setLeftPanelWidth] = useState(STUDIO_LEFT_PANEL_DEFAULT_WIDTH);
    const [rightPanelWidth, setRightPanelWidth] = useState(STUDIO_RIGHT_PANEL_DEFAULT_WIDTH);
    const hasProposalInThread = useMemo(() => chatMessages.some((message) => !!message.proposals?.length), [chatMessages]);

    const interpolateFallback = (fallback: string, options?: Record<string, unknown>) => {
        if (!options) {
            return fallback;
        }

        return fallback.replace(/\{\{(\w+)\}\}/g, (_, optionKey) => String(options[optionKey] ?? ""));
    };

    const tt = (key: string, fallback: string, options?: Record<string, unknown>) => {
        const value = options ? t(key, options as any) : t(key);
        const translated = String(value || "");
        return !translated || translated === key ? interpolateFallback(fallback, options) : translated;
    };

    const translateLabel = (value?: string | null, fallback?: string) => {
        if (!value) {
            return fallback || "";
        }

        const translated = t(value);
        return translated && translated !== value ? String(translated) : (fallback || value);
    };

    const packageQuery = useQuery({
        queryKey: queryKeys.records.detail("platform_packages", currentPlatformPackageId || NEW_RECORD_ID),
        queryFn: () => new RecordManagement("platform_packages").get(currentPlatformPackageId || ""),
        enabled: !!currentPlatformPackageId,
    });

    const studioContextQuery = useQuery({
        queryKey: ["platform-studio", "context", currentPlatformPackageId],
        queryFn: fetchStudioContext,
        enabled: !!userSession,
    });

    const aiModelsQuery = useQuery({
        queryKey: ["platform-studio", "ai-models", "architecture", currentPlatformPackageId],
        queryFn: () => fetchStudioAiModels("architecture"),
        enabled: !!userSession,
    });

    const studioSessionsQuery = useQuery({
        queryKey: ["platform-studio", "sessions", currentPlatformPackageId],
        queryFn: fetchStudioSessions,
        enabled: !!userSession && !!currentPlatformPackageId,
    });

    const studioConversationQuery = useQuery({
        queryKey: ["platform-studio", "conversation", selectedSessionId],
        queryFn: () => fetchStudioConversation(selectedSessionId),
        enabled: !!selectedSessionId,
    });

    const recordsQuery = useQuery({
        queryKey: ["platform-studio", "records", selectedTable],
        queryFn: () => new RecordManagement<Record<string, any>>(selectedTable)
            .paginate(1, 40)
            .orderBy("updated_at", "desc")
            .run(),
        enabled: !!selectedTable,
        retry: false,
    });

    const explorerPreferenceQuery = useQuery({
        queryKey: queryKeys.records.lookup("sys_user_preference", "studio-explorer", EXPLORER_PREFERENCE_TABLE, EXPLORER_PREFERENCE_KEY, userSession?.id ?? null),
        queryFn: async () => {
            const response = await new RecordManagement("sys_user_preference")
                .where("table_name", EXPLORER_PREFERENCE_TABLE)
                .where("user_id", userSession?.id)
                .where("preference_key", EXPLORER_PREFERENCE_KEY)
                .first();

            if (!response?.preference_value) {
                return null;
            }

            return typeof response.preference_value === "string"
                ? JSON.parse(response.preference_value)
                : response.preference_value;
        },
        enabled: !!userSession?.id,
    });

    const architectureMutation = useMutation({
        mutationFn: requestStudioArchitecture,
        onSuccess: (data) => {
            setArchitectureResult(data);
            setSelectedSessionId(data.session?.id || "");
            queryClient.invalidateQueries({ queryKey: ["platform-studio", "sessions", currentPlatformPackageId] });
            setChatMessages((current) => [
                ...current,
                {
                    id: `assistant-${Date.now()}`,
                    role: "assistant",
                    content: data.architecture.architecture_summary,
                    plan: data.plan,
                    architecture: data.architecture,
                },
            ]);
        },
        onError: (error: any) => {
            const requestCanceled = isRequestCanceled(error);
            setChatMessages((current) => [
                ...current,
                {
                    id: `assistant-error-${Date.now()}`,
                    role: "assistant",
                    content: requestCanceled
                        ? tt("studio.ai.stopped", "Request stopped.")
                        : error?.response?.data?.error?.message || error?.message || tt("studio.ai.error", "The architecture request failed."),
                },
            ]);
        },
        onSettled: () => {
            architectureAbortControllerRef.current = null;
        },
    });

    const proposalMutation = useMutation({
        mutationFn: generateStudioProposal,
        onSuccess: (data: StudioProposalResponse) => {
            setChatMessages((current) => [
                ...current,
                {
                    id: `assistant-proposal-${Date.now()}`,
                    role: "assistant",
                    content: data.summary,
                    plan: data.plan,
                    proposals: data.proposals,
                    proposalSummary: data.summary,
                },
            ]);
            queryClient.invalidateQueries({ queryKey: ["platform-studio", "context", currentPlatformPackageId] });
        },
        onError: (error: any) => {
            const requestCanceled = isRequestCanceled(error);
            setChatMessages((current) => [
                ...current,
                {
                    id: `assistant-proposal-error-${Date.now()}`,
                    role: "assistant",
                    content: requestCanceled
                        ? tt("studio.ai.stopped", "Request stopped.")
                        : error?.response?.data?.error?.message || error?.message || tt("studio.proposal.error", "The proposal request failed."),
                },
            ]);
        },
        onSettled: () => {
            proposalAbortControllerRef.current = null;
        },
    });

    const proposalStatusMutation = useMutation({
        mutationFn: updateStudioProposalStatus,
        onSuccess: (proposal) => {
            setChatMessages((current) => current.map((message) => ({
                ...message,
                proposals: message.proposals?.map((item) => (
                    item.id === proposal.id ? { ...item, status: proposal.status } : item
                )),
            })));
            queryClient.invalidateQueries({ queryKey: ["platform-studio", "context", currentPlatformPackageId] });
        },
    });

    const proposalApplyMutation = useMutation({
        mutationFn: applyStudioProposals,
        onSuccess: (data: StudioProposalApplyResponse) => {
            setChatMessages((current) => [
                ...current.map((message) => ({
                    ...message,
                    proposals: message.proposals?.map((proposal) => (
                        data.proposals.find((applied) => applied.id === proposal.id) || proposal
                    )),
                })),
                {
                    id: `assistant-apply-${Date.now()}`,
                    role: "assistant",
                    content: tt("studio.proposal.applied", "Applied {{count}} proposal changes to the active Platform Package.", { count: data.applied_count }),
                },
            ]);
            queryClient.invalidateQueries({ queryKey: ["platform-studio", "context", currentPlatformPackageId] });
        },
        onError: (error: any) => {
            setChatMessages((current) => [
                ...current,
                {
                    id: `assistant-apply-error-${Date.now()}`,
                    role: "assistant",
                    content: error?.response?.data?.error?.message || error?.message || tt("studio.proposal.apply_error", "Unable to apply proposal."),
                },
            ]);
        },
    });

    const saveExplorerPreferenceMutation = useMutation({
        mutationFn: async (preference: StudioExplorerPreference) => {
            if (!userSession?.id) {
                return;
            }

            const payload = JSON.stringify(preference);
            const response = await new RecordManagement("sys_user_preference")
                .where("table_name", EXPLORER_PREFERENCE_TABLE)
                .where("user_id", userSession.id)
                .where("preference_key", EXPLORER_PREFERENCE_KEY)
                .first();

            if (!response?.id) {
                return new RecordManagement("sys_user_preference").save({
                    user_id: userSession.id,
                    table_name: EXPLORER_PREFERENCE_TABLE,
                    preference_key: EXPLORER_PREFERENCE_KEY,
                    preference_value: payload,
                });
            }

            return new RecordManagement("sys_user_preference").update(response.id, {
                preference_value: payload,
            });
        },
        onSuccess: (_, preference) => {
            queryClient.setQueryData(
                queryKeys.records.lookup("sys_user_preference", "studio-explorer", EXPLORER_PREFERENCE_TABLE, EXPLORER_PREFERENCE_KEY, userSession?.id ?? null),
                preference
            );
        },
    });

    const packageRequired = !currentPlatformPackageId;
    const studioContext = studioContextQuery.data;
    const contextTables = studioContext?.metadata.tables ?? [];
    const contextColumns = studioContext?.metadata.columns ?? [];
    const records = recordsQuery.data?.data ?? [];
    const selectedTableMetadata = contextTables.find((table) => table.name === selectedTable) || null;
    const selectedTableColumns = contextColumns
        .filter((column) => column.table === selectedTable)
        .sort((first, second) => (first.order || 0) - (second.order || 0));
    const codeColumns = selectedTableColumns.filter((column) =>
        !systemFields.has(column.name) &&
        !column.is_hidden &&
        column.column_type === "CODE"
    );
    const selectedColumn = codeColumns.find((column) => column.name === selectedFieldName)
        || codeColumns[0]
        || null;
    const allTableModels = useMemo(() => contextTables.map(toSysTable), [contextTables]);
    const allColumnModels = useMemo(() => contextColumns.map(toSysColumn), [contextColumns]);
    const selectedTableModel = useMemo(
        () => selectedTableMetadata ? toSysTable(selectedTableMetadata) : null,
        [selectedTableMetadata]
    );
    const selectedColumnModel = useMemo(
        () => selectedColumn ? toSysColumn(selectedColumn) : null,
        [selectedColumn]
    );
    const jsonColumnModel = useMemo(() => new SysColumn({
        id: "platform-studio-record-json",
        sys_table_id: selectedTableModel?.id || "",
        active: true,
        label: tt("studio.view.json", "JSON"),
        name: "record_json",
        column_type: "CODE",
        column_subtype: "JSON",
        order: 0,
        referenced_table_name: "",
        reference_filter: "",
        currency_code: "",
        currency_decimal_places: null,
        default_value: "",
        is_display: false,
        is_hidden: false,
        is_mandatory: false,
        depends_on_column_id: "",
        created_at: new Date(),
        updated_at: new Date(),
    }), [selectedTableModel]);
    const selectedRecord = records.find((record: Record<string, any>) => record.id === selectedRecordId) || null;
    const currentFieldChanged = selectedColumn
        ? stringifyFieldValue(draftRecord[selectedColumn.name], selectedColumn) !== stringifyFieldValue(selectedRecord?.[selectedColumn.name], selectedColumn)
        : false;
    const changedFields = selectedTableColumns.filter((column) =>
        stringifyFieldValue(draftRecord[column.name], column) !== stringifyFieldValue(selectedRecord?.[column.name], column)
    );
    const packageName = useMemo(() => {
        if (!currentPlatformPackageId) return tt("studio.package.none", "No package selected");
        return (packageQuery.data as any)?.data?.name || tt("studio.package.loading", "Loading package");
    }, [currentPlatformPackageId, packageQuery.data]);
    const packageChanges = studioContext?.package_changes ?? [];
    const activeArchitecture = architectureResult?.architecture;
    const aiModels = (aiModelsQuery.data ?? []) as StudioAiModel[];
    const studioSessions = (studioSessionsQuery.data ?? []) as StudioSession[];
    const selectedAiModel = aiModels.find((model) => model.id === selectedAiModelId) || null;
    const studioPreference = explorerPreferenceQuery.data as StudioExplorerPreference | null;
    const favoriteAiModelId = studioPreference?.favoriteAiModelId || null;
    const favoriteAiProviderId = studioPreference?.favoriteAiProviderId || null;
    const hasActiveAi = (studioContext?.studio.ai_providers ?? [])
        .some((provider) => provider.active !== false && provider.active !== "false");
    const bodyGridTemplateColumns = useMemo(() => {
        const columns = [`${STUDIO_ACTIVITY_WIDTH}px`];

        if (leftPanelMode) {
            columns.push(`${leftPanelWidth}px`, `${STUDIO_RESIZE_HANDLE_WIDTH}px`);
        }

        columns.push("minmax(0, 1fr)");

        if (rightPanelMode) {
            columns.push(`${STUDIO_RESIZE_HANDLE_WIDTH}px`, `${rightPanelWidth}px`);
        }

        return columns.join(" ");
    }, [leftPanelMode, leftPanelWidth, rightPanelMode, rightPanelWidth]);
    const explorerTables = contextTables
        .filter((table) => table.name)
        .sort((first, second) => translateLabel(first.label, first.name).localeCompare(translateLabel(second.label, second.name)));
    const editorAvailable = Boolean(selectedRecordId && selectedColumn);
    const jsonAvailable = Boolean(selectedRecord);
    const recordJsonChanged = jsonAvailable && recordJsonValue !== safeRecordJson(selectedRecord);
    const hasUnsavedStudioChanges = (activeViewMode === "editor" && changedFields.length > 0) ||
        (activeViewMode === "json" && recordJsonChanged);

    const persistStudioPreference = (
        nextGroups = expandedGroups,
        nextTables = expandedTables,
        nextRecords = expandedRecords,
        overrides: Partial<StudioExplorerPreference> = {}
    ) => {
        if (!studioPreferenceApplied) {
            return;
        }

        saveExplorerPreferenceMutation.mutate({
            groups: setToArray(nextGroups),
            tables: setToArray(nextTables),
            records: setToArray(nextRecords),
            leftPanelMode,
            rightPanelMode,
            leftPanelWidth,
            rightPanelWidth,
            favoriteAiModelId,
            favoriteAiProviderId,
            ...overrides,
        });
    };

    const displayRecord = (record: Record<string, any>) => {
        const displayColumn = selectedTableColumns.find((column) => column.is_display && record[column.name] != null);
        const displayValue = displayColumn ? record[displayColumn.name] : null;

        return String(displayValue || record.label || record.name || record.title || record.number_ref || record.id || tt("studio.record.new", "New record"));
    };

    const syncSelectedFieldValue = (record: Record<string, any>, column: StudioColumnMetadata | null) => {
        setFieldValue(stringifyFieldValue(column ? record[column.name] : "", column));
    };

    const selectField = (fieldName: string) => {
        const nextColumn = codeColumns.find((column) => column.name === fieldName) || null;

        setSelectedFieldName(fieldName);
        syncSelectedFieldValue(draftRecord, nextColumn);
        setActiveViewMode("editor");
        setEditorError(null);
    };

    const selectTable = (tableName: string, toggleExpansion = true) => {
        if (toggleExpansion) {
            setExpandedTables((current) => {
                const next = new Set(current);
                const shouldCollapse = selectedTable === tableName && next.has(tableName);

                if (shouldCollapse) {
                    next.delete(tableName);
                } else {
                    next.add(tableName);
                }

                persistStudioPreference(expandedGroups, next, expandedRecords);
                return next;
            });
        }
        setSelectedTable(tableName);
        setSelectedRecordId(null);
        setSelectedFieldName(null);
        setDraftRecord({});
        setFieldValue("");
        setRecordJsonValue("{}");
        setEditorError(null);
    };

    const createRecordForTable = (tableName: string) => {
        setSelectedTable(tableName);
        setSelectedRecordId(null);
        setSelectedFieldName(null);
        setDraftRecord({});
        setFieldValue("");
        setRecordJsonValue("{}");
        setEditorError(null);
        setExpandedTables((current) => {
            const next = new Set(current);
            next.add(tableName);
            persistStudioPreference(expandedGroups, next, expandedRecords);
            return next;
        });
    };

    const selectRecord = (record: Record<string, any>, toggleExpansion = true) => {
        const nextField = selectedFieldName && codeColumns.some((column) => column.name === selectedFieldName)
            ? selectedFieldName
            : codeColumns[0]?.name || null;
        const nextColumn = codeColumns.find((column) => column.name === nextField) || null;
        const recordId = record.id || null;

        if (toggleExpansion) {
            setExpandedRecords((current) => {
                const next = new Set(current);
                const shouldCollapse = selectedRecordId === recordId && recordId && next.has(recordId);

                if (recordId && shouldCollapse) {
                    next.delete(recordId);
                } else if (recordId) {
                    next.add(recordId);
                }

                persistStudioPreference(expandedGroups, expandedTables, next);
                return next;
            });
        }

        setSelectedRecordId(recordId);
        setSelectedFieldName(nextField);
        setDraftRecord(record);
        syncSelectedFieldValue(record, nextColumn);
        setRecordJsonValue(safeRecordJson(record));
        setEditorError(null);
    };

    const setCurrentFieldValue = (value: string) => {
        setFieldValue(value);

        if (!selectedColumn) {
            return;
        }

        setDraftRecord((current) => ({
            ...current,
            [selectedColumn.name]: selectedColumn.column_type === "CODE" && selectedColumn.column_subtype === "JSON"
                ? value
                : value,
        }));
    };

    const normalizedDraftPayload = () => {
        const payload = { ...draftRecord };

        if (selectedColumn) {
            payload[selectedColumn.name] = parseFieldValue(fieldValue, selectedColumn);
        }

        return payload;
    };

    const saveRecordMutation = useMutation({
        mutationFn: async () => {
            setEditorError(null);

            let payload: Record<string, any>;

            if (activeViewMode === "json") {
                payload = JSON.parse(recordJsonValue || "{}");
            } else {
                payload = normalizedDraftPayload();
            }

            if (selectedRecordId) {
                return new RecordManagement<Record<string, any>>(selectedTable).update(selectedRecordId, payload);
            }

            return new RecordManagement<Record<string, any>>(selectedTable).save(payload);
        },
        onSuccess: (record: any) => {
            const nextRecord = record?.data || record;
            setSelectedRecordId(nextRecord?.id || selectedRecordId);
            setDraftRecord(nextRecord || {});
            syncSelectedFieldValue(nextRecord || {}, selectedColumn);
            setRecordJsonValue(safeRecordJson(nextRecord || {}));
            queryClient.invalidateQueries({ queryKey: ["platform-studio", "records", selectedTable] });
            queryClient.invalidateQueries({ queryKey: ["platform-studio", "context", currentPlatformPackageId] });
        },
        onError: (error: any) => {
            setEditorError(error?.response?.data?.error?.message || error?.message || tt("studio.error.save_record", "Unable to save record."));
        },
    });

    const deleteRecordMutation = useMutation({
        mutationFn: async () => {
            if (!selectedRecordId) return;
            return new RecordManagement(selectedTable).delete(selectedRecordId);
        },
        onSuccess: () => {
        setSelectedRecordId(null);
        setDraftRecord({});
        setFieldValue("");
        setRecordJsonValue("{}");
        setDeleteConfirmOpen(false);
        queryClient.invalidateQueries({ queryKey: ["platform-studio", "records", selectedTable] });
        queryClient.invalidateQueries({ queryKey: ["platform-studio", "context", currentPlatformPackageId] });
        setExpandedRecords((current) => {
            if (!selectedRecordId) {
                return current;
            }

            const next = new Set(current);
            next.delete(selectedRecordId);
            persistStudioPreference(expandedGroups, expandedTables, next);
            return next;
        });
    },
    });

    const submitArchitecture = (promptValue: string) => {
        const nextPrompt = promptValue.trim();
        if (!nextPrompt || packageRequired || architectureMutation.isPending || proposalMutation.isPending) return;

        if (hasProposalInThread && architectureResult?.plan?.id) {
            setChatMessages((current) => [
                ...current,
                {
                    id: `user-${Date.now()}`,
                    role: "user",
                    content: nextPrompt,
                },
            ]);
            generateProposalForPlan(architectureResult.plan, nextPrompt);
            return;
        }

        architectureAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        architectureAbortControllerRef.current = abortController;
        setChatMessages((current) => [
            ...current,
            {
                id: `user-${Date.now()}`,
                role: "user",
                content: nextPrompt,
            },
        ]);
        architectureMutation.mutate({
            prompt: nextPrompt,
            session_id: selectedSessionId || null,
            model_id: selectedAiModelId || null,
            signal: abortController.signal,
        });
    };

    const stopArchitectureRequest = () => {
        architectureAbortControllerRef.current?.abort();
        architectureAbortControllerRef.current = null;
    };

    const generateProposalForPlan = (plan?: Record<string, any> | null, refinementPrompt?: string | null) => {
        const planId = plan?.id;
        if (!planId || packageRequired || proposalMutation.isPending) return;

        proposalAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        proposalAbortControllerRef.current = abortController;
        proposalMutation.mutate({
            plan_id: planId,
            model_id: selectedAiModelId || null,
            prompt: refinementPrompt || null,
            signal: abortController.signal,
        });
    };

    const stopProposalRequest = () => {
        proposalAbortControllerRef.current?.abort();
        proposalAbortControllerRef.current = null;
    };

    const setProposalStatus = (proposal: StudioChangeProposal, status: "approved" | "rejected") => {
        proposalStatusMutation.mutate({
            id: proposal.id,
            status,
        });
    };

    const setProposalGroupStatus = (proposals: StudioChangeProposal[], status: "approved" | "rejected") => {
        proposals
            .filter((proposal) => proposal.status !== status)
            .forEach((proposal) => setProposalStatus(proposal, status));
    };

    const restoreConversation = (conversation: StudioConversationResponse) => {
        setSelectedSessionId(conversation.session.id);
        setArchitectureResult(conversation.plan ? {
            session: conversation.session,
            plan: conversation.plan,
            architecture: {
                phase: "architecture",
                package_id: currentPlatformPackageId || "",
                requirement_summary: conversation.plan.requirement_summary || "",
                architecture_summary: conversation.plan.architecture_summary || "",
                objects: safeParseJson(conversation.plan.objects_json, []),
                conditions: [],
                risks: safeParseJson(conversation.plan.risks_json, []),
            },
        } : null);
        setChatMessages(conversation.messages.map((message) => ({
            id: message.id,
            role: message.role,
            content: message.content,
            plan: message.plan,
            architecture: message.architecture || undefined,
            proposals: message.proposals,
            proposalSummary: message.proposal_summary || undefined,
        })));
    };

    const startNewConversation = () => {
        architectureAbortControllerRef.current?.abort();
        proposalAbortControllerRef.current?.abort();
        architectureAbortControllerRef.current = null;
        proposalAbortControllerRef.current = null;
        setSelectedSessionId("");
        setArchitectureResult(null);
        setChatMessages([]);
        setHistoryAutoLoaded(true);
    };

    const handleProposalApply = (proposals: StudioChangeProposal[]) => {
        const proposalIds = proposals
            .filter((proposal) => proposal.status === "approved")
            .map((proposal) => proposal.id);

        if (proposalIds.length === 0 || proposalApplyMutation.isPending) {
            return;
        }

        proposalApplyMutation.mutate({ proposal_ids: proposalIds });
    };

    const startPanelResize = (panel: "left" | "right", event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();

        const startX = event.clientX;
        const startLeftWidth = leftPanelWidth;
        const startRightWidth = rightPanelWidth;
        let nextLeftWidth = startLeftWidth;
        let nextRightWidth = startRightWidth;

        const applyGridTemplate = () => {
            if (!bodyRef.current) {
                return;
            }

            const columns = [`${STUDIO_ACTIVITY_WIDTH}px`];
            if (leftPanelMode) {
                columns.push(`${nextLeftWidth}px`, `${STUDIO_RESIZE_HANDLE_WIDTH}px`);
            }

            columns.push("minmax(0, 1fr)");

            if (rightPanelMode) {
                columns.push(`${STUDIO_RESIZE_HANDLE_WIDTH}px`, `${nextRightWidth}px`);
            }

            bodyRef.current.style.gridTemplateColumns = columns.join(" ");
        };

        const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
            if (panel === "left") {
                nextLeftWidth = clamp(
                    startLeftWidth + moveEvent.clientX - startX,
                    STUDIO_LEFT_PANEL_MIN_WIDTH,
                    STUDIO_LEFT_PANEL_MAX_WIDTH
                );
                applyGridTemplate();
                return;
            }

            nextRightWidth = clamp(
                startRightWidth + startX - moveEvent.clientX,
                STUDIO_RIGHT_PANEL_MIN_WIDTH,
                STUDIO_RIGHT_PANEL_MAX_WIDTH
            );
            applyGridTemplate();
        };

        const handleMouseUp = () => {
            setLeftPanelWidth(nextLeftWidth);
            setRightPanelWidth(nextRightWidth);
            persistStudioPreference(expandedGroups, expandedTables, expandedRecords, {
                leftPanelWidth: nextLeftWidth,
                rightPanelWidth: nextRightWidth,
            });
            document.body.classList.remove("platform-studio-resizing");
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };

        document.body.classList.add("platform-studio-resizing");
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
    };

    const copyFieldPath = () => {
        if (!selectedColumn) {
            return;
        }

        navigator.clipboard?.writeText(`${selectedTable}.${selectedColumn.name}`);
    };

    const focusPackageChange = (resourceKey?: string | null) => {
        const tableName = packageChangeTableName(resourceKey);

        if (tableName) {
            applyLeftPanelMode("explorer");
            selectTable(tableName, false);
        }
    };

    const applyLeftPanelMode = (nextMode: StudioPanelMode) => {
        setLeftPanelMode(nextMode);
        persistStudioPreference(expandedGroups, expandedTables, expandedRecords, { leftPanelMode: nextMode });
    };

    const requestLeftPanelMode = (nextMode: StudioPanelMode) => {
        if (hasUnsavedStudioChanges) {
            setPendingLeftPanelMode(nextMode);
            setDiscardConfirmOpen(true);
            return;
        }

        applyLeftPanelMode(nextMode);
    };

    const changeViewMode = (_event: MouseEvent<HTMLElement>, nextMode: StudioViewMode | null) => {
        if (!nextMode) {
            return;
        }

        if (nextMode === "editor" && !editorAvailable) {
            return;
        }

        if (nextMode === "json" && !jsonAvailable) {
            return;
        }

        setEditorError(null);
        setActiveViewMode(nextMode);
    };

    const discardAndApplyPendingPanel = () => {
        if (selectedRecord) {
            setDraftRecord(selectedRecord);
            syncSelectedFieldValue(selectedRecord, selectedColumn);
            setRecordJsonValue(safeRecordJson(selectedRecord));
        }

        applyLeftPanelMode(pendingLeftPanelMode);
        setPendingLeftPanelMode(null);
        setDiscardConfirmOpen(false);
    };

    const handleActivityClick = (activityId: string) => {
        if (activityId === "package") {
            requestLeftPanelMode(leftPanelMode === "package" ? null : "package");
            return;
        }

        if (activityId === "explorer") {
            requestLeftPanelMode(leftPanelMode === "explorer" ? null : "explorer");
            return;
        }

        if (activityId === "architecture" || activityId === "ai") {
            setRightPanelMode((current) => {
                const nextMode = current === activityId ? null : activityId as StudioRightPanelMode;
                persistStudioPreference(expandedGroups, expandedTables, expandedRecords, { rightPanelMode: nextMode });
                return nextMode;
            });
        }
    };

    const markSelectedAiFavorite = () => {
        const providerId = selectedAiModel?.provider?.id || null;

        persistStudioPreference(expandedGroups, expandedTables, expandedRecords, {
            favoriteAiModelId: selectedAiModelId || null,
            favoriteAiProviderId: providerId,
        });
    };

    useEffect(() => {
        if (!selectedFieldName && codeColumns.length > 0) {
            const nextField = codeColumns[0].name;
            setSelectedFieldName(nextField);
        }
    }, [codeColumns, selectedFieldName]);

    useEffect(() => {
        if (activeViewMode === "editor" && !editorAvailable) {
            setActiveViewMode("record");
        }

        if (activeViewMode === "json" && !jsonAvailable) {
            setActiveViewMode("record");
        }
    }, [activeViewMode, editorAvailable, jsonAvailable]);

    useEffect(() => {
        if (!selectedAiModelId && aiModels.length > 0) {
            const favoriteModel = favoriteAiModelId
                ? aiModels.find((model) => model.id === favoriteAiModelId)
                : null;
            const favoriteProviderModel = favoriteAiProviderId
                ? aiModels.find((model) => model.provider?.id === favoriteAiProviderId)
                : null;

            setSelectedAiModelId((favoriteModel || favoriteProviderModel || aiModels[0]).id);
            return;
        }

        if (selectedAiModelId && aiModels.length > 0 && !aiModels.some((model) => model.id === selectedAiModelId)) {
            const favoriteProviderModel = favoriteAiProviderId
                ? aiModels.find((model) => model.provider?.id === favoriteAiProviderId)
                : null;

            setSelectedAiModelId((favoriteProviderModel || aiModels[0]).id);
        }
    }, [aiModels, favoriteAiModelId, favoriteAiProviderId, selectedAiModelId]);

    useEffect(() => {
        if (selectedColumn) {
            syncSelectedFieldValue(draftRecord, selectedColumn);
        }
    }, [selectedColumn?.name]);

    useEffect(() => {
        if (!hasActiveAi) {
            setRightPanelMode(null);
            persistStudioPreference(expandedGroups, expandedTables, expandedRecords, { rightPanelMode: null });
        }
    }, [hasActiveAi]);

    useEffect(() => {
        setHistoryAutoLoaded(false);
        setSelectedSessionId("");
        setChatMessages([]);
        setArchitectureResult(null);
    }, [currentPlatformPackageId]);

    useEffect(() => {
        if (historyAutoLoaded || selectedSessionId || chatMessages.length > 0 || studioSessions.length === 0) {
            return;
        }

        setSelectedSessionId(studioSessions[0].id);
        setHistoryAutoLoaded(true);
    }, [chatMessages.length, historyAutoLoaded, selectedSessionId, studioSessions]);

    useEffect(() => {
        if (!studioConversationQuery.data) {
            return;
        }

        restoreConversation(studioConversationQuery.data as StudioConversationResponse);
    }, [studioConversationQuery.data]);

    useEffect(() => {
        setStudioPreferenceApplied(false);
    }, [userSession?.id]);

    useEffect(() => {
        if (!userSession?.id || studioPreferenceApplied || explorerPreferenceQuery.isLoading) {
            return;
        }

        const preference = explorerPreferenceQuery.data as StudioExplorerPreference | null;

        setExpandedGroups(new Set(normalizePreferenceSet(preference?.groups)));
        setExpandedTables(new Set(normalizePreferenceSet(preference?.tables)));
        setExpandedRecords(new Set(normalizePreferenceSet(preference?.records)));
        setLeftPanelMode(preference?.leftPanelMode === "package" || preference?.leftPanelMode === null ? preference.leftPanelMode : "explorer");
        setRightPanelMode(preference?.rightPanelMode === "architecture" || preference?.rightPanelMode === "ai" ? preference.rightPanelMode : null);
        setLeftPanelWidth(clamp(
            Number(preference?.leftPanelWidth) || STUDIO_LEFT_PANEL_DEFAULT_WIDTH,
            STUDIO_LEFT_PANEL_MIN_WIDTH,
            STUDIO_LEFT_PANEL_MAX_WIDTH
        ));
        setRightPanelWidth(clamp(
            Number(preference?.rightPanelWidth) || STUDIO_RIGHT_PANEL_DEFAULT_WIDTH,
            STUDIO_RIGHT_PANEL_MIN_WIDTH,
            STUDIO_RIGHT_PANEL_MAX_WIDTH
        ));

        if (preference?.favoriteAiModelId) {
            setSelectedAiModelId(preference.favoriteAiModelId);
        }

        setStudioPreferenceApplied(true);
    }, [
        studioPreferenceApplied,
        explorerPreferenceQuery.data,
        explorerPreferenceQuery.isLoading,
        userSession?.id,
    ]);

    return (
        <Box className="platform-studio">
            <StudioTopBar
                packageName={packageName}
                packageRequired={packageRequired}
                saveDisabled={packageRequired || saveRecordMutation.isPending || activeViewMode === "record"}
                deleteDisabled={!selectedRecordId || packageRequired || deleteRecordMutation.isPending}
                tt={tt}
                onSave={() => saveRecordMutation.mutate()}
                onDelete={() => setDeleteConfirmOpen(true)}
            />

            <Box
                ref={bodyRef}
                className={[
                    "platform-studio__body",
                    !leftPanelMode ? "platform-studio__body--left-hidden" : "",
                    !rightPanelMode ? "platform-studio__body--right-hidden" : "",
                ].filter(Boolean).join(" ")}
                style={{ gridTemplateColumns: bodyGridTemplateColumns }}
            >
                <StudioActivityRail
                    hasActiveAi={hasActiveAi}
                    leftPanelMode={leftPanelMode}
                    rightPanelMode={rightPanelMode}
                    tt={tt}
                    onActivityClick={handleActivityClick}
                />

                {leftPanelMode ? (
                <Box key={leftPanelMode} className="platform-studio__explorer platform-studio__panel--enter">
                    <Typography className="platform-studio__panel-title">
                        {leftPanelMode === "package"
                            ? tt("studio.explorer.package_changes", "Current Package")
                            : tt("studio.explorer.title", "Explorer")}
                    </Typography>
                    <Divider />
                    <Box className="platform-studio__tree">
                        {studioContextQuery.isLoading ? (
                            <Box className="platform-studio__loading-row">
                                <CircularProgress size={16} />
                                <Typography variant="body2">{tt("studio.context.loading", "Loading Studio context")}</Typography>
                            </Box>
                        ) : null}

                        {leftPanelMode === "package" ? (
                            <Box className="platform-studio__tree-group platform-studio__tree-group--package">
                            {packageChanges.length === 0 ? (
                                <Typography className="platform-studio__empty-tree-copy">
                                    {tt("studio.package.no_changes", "No package changes")}
                                </Typography>
                            ) : packageChanges.slice(0, 12).map((change) => (
                                <button
                                    key={change.id}
                                    className="platform-studio__tree-item platform-studio__tree-item--package"
                                    onClick={() => focusPackageChange(change.resource_key)}
                                    type="button"
                                >
                                    <Difference fontSize="inherit" />
                                    <span>{change.resource_key}</span>
                                </button>
                            ))}
                            </Box>
                        ) : null}

                        {leftPanelMode === "explorer" ? (
                            <Box className="platform-studio__tree-group">
                                {explorerTables.map((tableMetadata) => {
                                    const item = tableMetadata.name;
                                    const tableLabel = translateLabel(tableMetadata.label, tableMetadata.name);
                                    const isSelectedTable = item === selectedTable;
                                    const isTableExpanded = expandedTables.has(item);

                                    return (
                                        <Box key={item} className="platform-studio__tree-node">
                                            <Box className={isSelectedTable ? "platform-studio__tree-item-row platform-studio__tree-item-row--active" : "platform-studio__tree-item-row"}>
                                            <button
                                                className={isSelectedTable ? "platform-studio__tree-item platform-studio__tree-item--active" : "platform-studio__tree-item"}
                                                onClick={() => selectTable(item)}
                                                type="button"
                                            >
                                                {isTableExpanded ? <KeyboardArrowDown fontSize="inherit" /> : <KeyboardArrowRight fontSize="inherit" />}
                                                <DataObject fontSize="inherit" />
                                                <span>{tableLabel}</span>
                                            </button>
                                            <Box className="platform-studio__tree-actions">
                                                <Tooltip title={tt("studio.action.new_record", "New record")}>
                                                    <IconButton size="small" onClick={() => createRecordForTable(item)}>
                                                        <Add fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={tt("studio.action.reload_records", "Reload records")}>
                                                    <IconButton size="small" onClick={() => item === selectedTable ? recordsQuery.refetch() : selectTable(item, false)}>
                                                        <Refresh fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            </Box>
                                            {isSelectedTable && isTableExpanded ? (
                                                <Box className="platform-studio__tree-children">
                                                    {recordsQuery.isLoading ? (
                                                        <Box className="platform-studio__loading-row platform-studio__loading-row--compact">
                                                            <CircularProgress size={12} />
                                                            <Typography variant="body2">{tt("studio.loading", "Loading")}</Typography>
                                                        </Box>
                                                    ) : records.map((record: Record<string, any>) => (
                                                        <Box key={record.id} className="platform-studio__tree-node">
                                                            <button
                                                                className={record.id === selectedRecordId ? "platform-studio__tree-item platform-studio__tree-item--record platform-studio__tree-item--active" : "platform-studio__tree-item platform-studio__tree-item--record"}
                                                                onClick={() => selectRecord(record)}
                                                                type="button"
                                                            >
                                                                {expandedRecords.has(record.id) ? <KeyboardArrowDown fontSize="inherit" /> : <KeyboardArrowRight fontSize="inherit" />}
                                                                <Article fontSize="inherit" />
                                                                <span>{displayRecord(record)}</span>
                                                                {record.id === selectedRecordId && changedFields.length > 0 ? <i /> : null}
                                                            </button>
                                                            {record.id === selectedRecordId && expandedRecords.has(record.id) ? (
                                                                <Box className="platform-studio__tree-children platform-studio__tree-children--fields">
                                                                    {codeColumns.map((column) => (
                                                                        <button
                                                                            key={column.id}
                                                                            className={column.name === selectedColumn?.name ? "platform-studio__tree-item platform-studio__tree-item--field platform-studio__tree-item--active" : "platform-studio__tree-item platform-studio__tree-item--field"}
                                                                            onClick={() => selectField(column.name)}
                                                                            type="button"
                                                                        >
                                                                            <Code fontSize="inherit" />
                                                                            <span>{translateLabel(column.label, column.name)}</span>
                                                                            {changedFields.some((changed) => changed.name === column.name) ? <i /> : null}
                                                                        </button>
                                                                    ))}
                                                                </Box>
                                                            ) : null}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : null}
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : null}
                    </Box>
                </Box>
                ) : null}

                {leftPanelMode ? (
                    <StudioResizeHandle side="left" onMouseDown={(event) => startPanelResize("left", event)} />
                ) : null}

                <StudioWorkbench
                    selectedTable={selectedTable}
                    selectedRecordId={selectedRecordId}
                    selectedRecord={selectedRecord}
                    selectedTableMetadata={selectedTableMetadata}
                    selectedColumn={selectedColumn}
                    selectedTableModel={selectedTableModel}
                    selectedColumnModel={selectedColumnModel}
                    jsonColumnModel={jsonColumnModel}
                    draftRecord={draftRecord}
                    recordsCount={records.length}
                    changedCount={changedFields.length}
                    fieldValue={fieldValue}
                    recordJsonValue={recordJsonValue}
                    activeViewMode={activeViewMode}
                    editorAvailable={editorAvailable}
                    jsonAvailable={jsonAvailable}
                    packageRequired={packageRequired}
                    currentFieldChanged={currentFieldChanged}
                    editorError={editorError}
                    allTableModels={allTableModels}
                    allColumnModels={allColumnModels}
                    studioConfig={studioContext?.studio}
                    tt={tt}
                    translateLabel={translateLabel}
                    displayRecord={displayRecord}
                    onViewModeChange={changeViewMode}
                    onCopyFieldPath={copyFieldPath}
                    onFieldValueChange={setCurrentFieldValue}
                    onRecordJsonChange={setRecordJsonValue}
                    onEditorValidityChange={setEditorError}
                />

                {rightPanelMode ? (
                <>
                <StudioResizeHandle side="right" onMouseDown={(event) => startPanelResize("right", event)} />
                <StudioAiPanel
                    mode={rightPanelMode}
                    aiModels={aiModels}
                    selectedAiModel={selectedAiModel}
                    selectedAiModelId={selectedAiModelId}
                    favoriteAiModelId={favoriteAiModelId}
                    favoriteAiProviderId={favoriteAiProviderId}
                    selectedSessionId={selectedSessionId}
                    studioSessions={studioSessions}
                    aiModelsLoading={aiModelsQuery.isLoading}
                    conversationFetching={studioConversationQuery.isFetching}
                    architecturePending={architectureMutation.isPending}
                    proposalPending={proposalMutation.isPending}
                    proposalStatusPending={proposalStatusMutation.isPending}
                    proposalApplyPending={proposalApplyMutation.isPending}
                    chatMessages={chatMessages}
                    hasProposalInThread={hasProposalInThread}
                    packageRequired={packageRequired}
                    tt={tt}
                    onModelChange={setSelectedAiModelId}
                    onFavoriteAiSelection={markSelectedAiFavorite}
                    onSessionChange={setSelectedSessionId}
                    onStartNewConversation={startNewConversation}
                    onGenerateProposal={generateProposalForPlan}
                    onStopProposal={stopProposalRequest}
                    onStopArchitecture={stopArchitectureRequest}
                    onSetProposalGroupStatus={setProposalGroupStatus}
                    onApplyProposal={handleProposalApply}
                    onPromptSubmit={submitArchitecture}
                />
                </>
                ) : null}
            </Box>
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>{tt("studio.delete.title", "Delete record")}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {tt("studio.delete.confirm", "Confirm deletion of the selected record?")}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
                        {tt("studio.action.cancel", "Cancel")}
                    </Button>
                    <Button color="error" variant="contained" onClick={() => deleteRecordMutation.mutate()}>
                        {tt("studio.action.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)}>
                <DialogTitle>{tt("studio.unsaved.title", "Unsaved changes")}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {tt("studio.unsaved.confirm", "You have unsaved changes in the current code field. Continue and discard them?")}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setDiscardConfirmOpen(false)}>
                        {tt("studio.action.cancel", "Cancel")}
                    </Button>
                    <Button color="warning" variant="contained" onClick={discardAndApplyPendingPanel}>
                        {tt("studio.action.discard", "Discard changes")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PlatformStudioPage;
